import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogBySlug, BLOG_POSTS, BlogPost } from '../../data/blogsData';
import './Blogs.css';

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [copied, setCopied] = useState<boolean>(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const post: BlogPost | undefined = useMemo(() => {
    if (!slug) return undefined;
    return getBlogBySlug(slug);
  }, [slug]);

  // Update SEO Meta Tags
  useEffect(() => {
    if (post) {
      document.title = post.metaTitle;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', post.metaDescription);
      }
      window.scrollTo(0, 0);
    }
  }, [post]);

  // Handle Copy Link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Related posts
  const relatedPosts: BlogPost[] = useMemo(() => {
    if (!post) return [];
    if (post.relatedSlugs && post.relatedSlugs.length > 0) {
      return post.relatedSlugs
        .map((s) => BLOG_POSTS.find((p) => p.slug === s))
        .filter((p): p is BlogPost => p !== undefined)
        .slice(0, 3);
    }
    return BLOG_POSTS.filter((p) => p.id !== post.id).slice(0, 3);
  }, [post]);

  if (!post) {
    return (
      <div className="blogs-page-wrapper">
        <div className="blogs-container" style={{ padding: '80px 24px', textAlign: 'center' }}>
          <div className="blogs-empty-state">
            <i className="fa fa-exclamation-triangle"></i>
            <h2>Article Not Found</h2>
            <p>The blog guide you are looking for might have been moved or updated.</p>
            <Link to="/blogs" className="btn-reset-filter" style={{ display: 'inline-block', textDecoration: 'none' }}>
              <i className="fa fa-arrow-left"></i> Back to All Guides
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentUrl = encodeURIComponent(window.location.href);
  const encodedTitle = encodeURIComponent(post.title);

  // Collect headings for Table of Contents
  const tocHeadings = post.sections
    .filter((s) => s.heading && s.heading !== 'Overview')
    .map((s, idx) => ({
      id: `section-${idx}`,
      title: s.heading!,
    }));

  return (
    <div className="blog-detail-wrapper">
      <div className="blogs-container">
        {/* Breadcrumb Navigation */}
        <nav className="blog-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="divider">/</span>
          <Link to="/blogs">Blogs</Link>
          <span className="divider">/</span>
          <span className="current">{post.title}</span>
        </nav>

        {/* Layout: Main Article + Sidebar */}
        <div className="blog-detail-layout">
          {/* Main Article Container */}
          <article className="blog-article-container">
            {/* Header */}
            <header className="blog-detail-header">
              <span className="blog-category-badge">{post.category}</span>
              <h1>{post.title}</h1>

              <div className="blog-author-meta">
                <div className="blog-author-left">
                  <div className="author-detail-avatar">
                    {post.author.charAt(0)}
                  </div>
                  <div className="author-meta-text">
                    <p className="name">{post.author}</p>
                    <p className="date">
                      Published in {post.publishDate} • {post.readTime}
                    </p>
                  </div>
                </div>

                {/* Social Share Buttons */}
                <div className="blog-share-buttons">
                  <span className="share-label">Share:</span>
                  <a
                    href={`https://wa.me/?text=${encodedTitle}%20${currentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn wa"
                    title="Share on WhatsApp"
                  >
                    <i className="fa fa-whatsapp"></i>
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn in"
                    title="Share on LinkedIn"
                  >
                    <i className="fa fa-linkedin"></i>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${currentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn tw"
                    title="Share on X (Twitter)"
                  >
                    <i className="fa fa-twitter"></i>
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="share-btn"
                    title="Copy Article Link"
                  >
                    <i className={copied ? 'fa fa-check' : 'fa fa-link'}></i>
                  </button>
                </div>
              </div>
            </header>

            {/* Key Summary Box */}
            {post.summary && (
              <div className="blog-summary-callout">
                <h4>
                  <i className="fa fa-lightbulb-o"></i> Executive Summary
                </h4>
                <p>{post.summary}</p>
              </div>
            )}

            {/* Article Body Content */}
            <div className="blog-article-body">
              {post.sections.map((section, sIdx) => {
                const headingId = `section-${sIdx}`;
                const isMiddle = sIdx === Math.floor(post.sections.length / 2);

                return (
                  <React.Fragment key={sIdx}>
                    {/* Render In-article Consultation CTA in the middle */}
                    {isMiddle && (
                      <div className="in-article-cta">
                        <h4>Need Commercial Car Financing in Maharashtra?</h4>
                        <p>
                          Get customized EMI options, low down-payment schemes, and fast documentation support through ASW Cars & Finance.
                        </p>
                        <div className="cta-links">
                          <a href="tel:+919987828690" className="btn-consult-call">
                            <i className="fa fa-phone"></i> +91 99878 28690
                          </a>
                          <a
                            href="https://wa.me/919987828417"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-consult-wa"
                          >
                            <i className="fa fa-whatsapp"></i> Chat on WhatsApp
                          </a>
                        </div>
                      </div>
                    )}

                    {section.heading && section.heading !== 'Overview' && (
                      <h2 id={headingId}>{section.heading}</h2>
                    )}

                    {section.subheading && <h3>{section.subheading}</h3>}

                    {section.paragraphs &&
                      section.paragraphs.map((p, pIdx) => (
                        <p key={pIdx}>{p}</p>
                      ))}

                    {section.bullets && section.bullets.length > 0 && (
                      <ul>
                        {section.bullets.map((b, bIdx) => (
                          <li key={bIdx}>{b}</li>
                        ))}
                      </ul>
                    )}

                    {/* Table Render */}
                    {section.table && section.table.headers.length > 0 && (
                      <div className="blog-table-wrapper">
                        <table className="blog-data-table">
                          <thead>
                            <tr>
                              {section.table.headers.map((h, hIdx) => (
                                <th key={hIdx}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.table.rows.map((row, rIdx) => (
                              <tr key={rIdx}>
                                {row.map((cell, cIdx) => (
                                  <td key={cIdx}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {/* FAQs Accordion if any */}
              {post.faqs && post.faqs.length > 0 && (
                <div className="blog-faq-accordion">
                  <h2>Frequently Asked Questions</h2>
                  {post.faqs.map((faq, fIdx) => (
                    <div
                      key={fIdx}
                      className={`faq-item ${openFaqIndex === fIdx ? 'open' : ''}`}
                    >
                      <button
                        type="button"
                        className="faq-question"
                        onClick={() => toggleFaq(fIdx)}
                      >
                        <span>{faq.question}</span>
                        <i className="fa fa-chevron-down"></i>
                      </button>
                      {openFaqIndex === fIdx && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Back link */}
            <div className="back-to-blogs-nav">
              <Link to="/blogs" className="btn-back-blogs">
                <i className="fa fa-arrow-left"></i> Back to All Blog Guides
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="blog-detail-sidebar">
            {/* Table of Contents */}
            {tocHeadings.length > 0 && (
              <div className="sidebar-card">
                <h4>Table of Contents</h4>
                <ul className="toc-list">
                  {tocHeadings.map((heading) => (
                    <li key={heading.id}>
                      <a href={`#${heading.id}`}>{heading.title}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Direct Loan Consultation Card */}
            <div className="sidebar-contact-card">
              <h4>Get Commercial Vehicle Loan</h4>
              <p>
                Get competitive commercial vehicle loan rates, fast approval, and complete registration assistance at ASW Cars Thane.
              </p>
              <a href="tel:+919987828690" className="sidebar-btn btn-call">
                <i className="fa fa-phone"></i> Call +91 99878 28690
              </a>
              <a
                href="https://wa.me/919987828417?text=Hello%20ASW%20Cars,%20I%20need%20assistance%20with%20commercial%20vehicle%20finance."
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-btn btn-wa"
              >
                <i className="fa fa-whatsapp"></i> WhatsApp Inquiry
              </a>
            </div>
          </aside>
        </div>

        {/* Related Articles (No Images) */}
        {relatedPosts.length > 0 && (
          <section className="related-blogs-section">
            <h2>Related Commercial Vehicle Guides</h2>
            <div className="blogs-grid">
              {relatedPosts.map((related) => (
                <Link
                  to={`/blogs/${related.slug}`}
                  key={related.id}
                  className="blog-card blog-card-no-img"
                >
                  <div className="blog-card-body">
                    <div className="blog-meta-row">
                      <span className="blog-category-badge">{related.category}</span>
                      <span>•</span>
                      <span><i className="fa fa-clock-o"></i> {related.readTime}</span>
                    </div>
                    <h3>{related.title}</h3>
                    <p>{related.summary}</p>
                    <div className="blog-card-footer">
                      <span className="author-name">{related.author}</span>
                      <span className="read-more-btn">
                        Read Article <i className="fa fa-arrow-right"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
